import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { map, Observable } from 'rxjs';

import {
  AutomaticAdviceCsvUploadResponse,
  AutomaticAdviceCustomersPreferencesLastUpdateResponse,
} from 'src/app/shared/models';

import { Environment } from 'src/environments/environment.model';

import {
  ControlType,
  mapToEnum,
  SchedulerState,
  SchedulerStateResponse,
} from '../models/control-room-models';

@Injectable({
  providedIn: 'root',
})
export class ControlRoomService {
  protected http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  private readonly arableCustomerBaseUrl =
    this.env.APIM_BASE_URL + '/arable-service/organisation/companysettings';
  private readonly arableAdviceBaseUrl = this.env.APIM_BASE_URL + '/arable-advice-service';
  private readonly automaticSchedulerBaseUrl = this.arableAdviceBaseUrl + '/automatic-scheduler';
  private readonly orderPlanningBaseUrl = this.env.API_BASE_URL + '/service/orderplanning/secure';

  triggerRecalculateBbaAdvice(year: number): Observable<void> {
    return this.http.post<void>(
      `${this.arableAdviceBaseUrl}/nutrient-calculation/trigger-recalculation/${year}`,
      {},
    );
  }

  triggerScheduler(schedulerType: ControlType): Observable<void> {
    return this.http.post<void>(`${this.automaticSchedulerBaseUrl}/${schedulerType}/trigger`, {});
  }

  setEnabledStateScheduler(
    schedulerType: ControlType,
    enabled: boolean,
  ): Observable<SchedulerState> {
    return this.http
      .put<SchedulerStateResponse>(
        `${this.automaticSchedulerBaseUrl}/${schedulerType}/enabled-state`,
        { enabled },
      )
      .pipe(
        map((response) => ({
          enabled: response.enabled,
          type: mapToEnum(response.type),
        })),
      );
  }

  getSchedulerEnabledState(schedulerType: ControlType): Observable<boolean> {
    return this.http.get<boolean>(`${this.automaticSchedulerBaseUrl}/${schedulerType}`);
  }

  uploadCompanySettingsFromCsv(file: File): Observable<AutomaticAdviceCsvUploadResponse> {
    const formData = new FormData();
    formData.append('Csv', file, file.name);
    return this.http.post<AutomaticAdviceCsvUploadResponse>(
      `${this.arableCustomerBaseUrl}/update-from-csv`,
      formData,
    );
  }

  setAutomaticAdviceCustomersPreferences(): Observable<void> {
    return this.http.post<void>(
      `${this.orderPlanningBaseUrl}/customerpreference/automatic-advice-customers`,
      {},
    );
  }

  getAutomaticAdviceCustomersPreferencesLastUpdate(): Observable<AutomaticAdviceCustomersPreferencesLastUpdateResponse> {
    return this.http.get<AutomaticAdviceCustomersPreferencesLastUpdateResponse>(
      `${this.orderPlanningBaseUrl}/customerpreference/automatic-advice-customers/last-update`,
    );
  }

  resetAutomaticAdviceForAllCustomers() {
    return this.http.post<void>(
      `${this.arableCustomerBaseUrl}/reset-all-customers`,
      {},
    );
  }
}
